<template>
  <a-drawer
    :open="visible"
    width="900"
    title="用户详情"
    @close="emit('update:visible', false)"
  >
    <template v-if="user">
      <a-descriptions bordered size="small" :column="2">
        <a-descriptions-item label="微信昵称">{{ user.wechat_nickname }}</a-descriptions-item>
        <a-descriptions-item label="微信号">{{ user.wechat_id || '—' }}</a-descriptions-item>
        <a-descriptions-item label="状态">
          <a-tag :color="user.status === 'active' ? 'green' : 'default'">
            {{ user.status === 'active' ? '正常' : '已停用' }}
          </a-tag>
        </a-descriptions-item>
        <a-descriptions-item label="最近到期">{{ user.nearest_expire_at || '—' }}</a-descriptions-item>
      </a-descriptions>

      <a-row :gutter="12" style="margin-top:16px">
        <a-col :span="6"><a-statistic title="当前节点" :value="user.proxy_count" /></a-col>
        <a-col :span="6"><a-statistic title="正常节点" :value="user.active_proxy_count" /></a-col>
        <a-col :span="6"><a-statistic title="累计上传" :value="formatBytes(user.bytes_up)" /></a-col>
        <a-col :span="6"><a-statistic title="累计下载" :value="formatBytes(user.bytes_down)" /></a-col>
      </a-row>

      <a-tabs v-model:active-key="activeTab" style="margin-top:16px">
        <a-tab-pane key="current" :tab="`当前节点 (${currentProxies.length})`">
          <ProxyTable :items="currentProxies" :loading="loading" @select="emit('select-proxy', $event)" />
        </a-tab-pane>
        <a-tab-pane key="deleted" :tab="`已删除记录 (${deletedProxies.length})`">
          <ProxyTable :items="deletedProxies" :loading="loading" @select="emit('select-proxy', $event)" />
        </a-tab-pane>
        <a-tab-pane key="disconnects" :tab="`断线排查 (${disconnectReport?.total ?? 0})`">
          <div class="disconnect-toolbar">
            <span>近 24 小时 · 当前归属代理 · 游戏端口连接结束记录</span>
            <a-button size="small" :loading="disconnectLoading" @click="loadDisconnects">刷新</a-button>
            <a-button size="small" :disabled="!disconnectReport" @click="copyReport('internal')">复制排查摘要</a-button>
            <a-button size="small" type="primary" :disabled="!disconnectReport" @click="copyReport('customer')">复制用户说明</a-button>
            <a-button size="small" :loading="analysisLoading" :disabled="!disconnectReport" @click="runAnalysis()">AI 分析</a-button>
          </div>
          <a-alert v-if="disconnectError" type="error" :message="disconnectError" show-icon style="margin-bottom:12px" />
          <a-alert v-if="analysisError" type="error" :message="analysisError" show-icon style="margin-bottom:12px" />
          <div v-if="analysis" class="analysis-card">
            <div class="analysis-head">
              <strong>掉线分析</strong>
              <a-tag :color="analysis.source === 'ai' ? 'purple' : 'default'">{{ sourceLabel(analysis) }}</a-tag>
              <a-tag v-if="analysisPending" color="processing">AI 总结生成中，通常需要一分钟左右…</a-tag>
              <span v-if="analysis.ai_error" class="analysis-note">{{ analysis.ai_error }}</span>
              <span v-else-if="!analysis.ai_configured" class="analysis-note">服务器未配置 AI，当前为规则分析</span>
              <span class="analysis-actions">
                <a-button size="small" @click="copyAnalysis('internal')">复制分析</a-button>
                <a-button size="small" :disabled="!analysis.customer_text" @click="copyAnalysis('customer')">复制 AI 用户说明</a-button>
              </span>
            </div>
            <p class="analysis-summary">{{ analysis.summary }}</p>
            <div v-if="analysis.customer_text" class="analysis-customer">
              <small>用户说明</small>
              <p>{{ analysis.customer_text }}</p>
            </div>
            <div v-if="analysis.hypotheses.length" class="analysis-hypotheses">
              <div v-for="(item, index) in analysis.hypotheses" :key="item.id" class="analysis-hypothesis">
                <div>
                  <strong>{{ index + 1 }}. {{ item.title }}</strong>
                  <a-tag :color="confidenceColor(item.confidence)" style="margin-left:6px">{{ confidenceLabel(item.confidence) }}</a-tag>
                </div>
                <div v-for="line in item.evidence" :key="`e-${line}`" class="analysis-for">支持：{{ line }}</div>
                <div v-for="line in item.against" :key="`a-${line}`" class="analysis-against">反对：{{ line }}</div>
              </div>
            </div>
            <div v-if="analysis.facts.nodes.length" class="analysis-peers">
              <small>同节点对比</small>
              <div v-for="node in analysis.facts.nodes" :key="node.alias">{{ nodeComparisonLine(node) }}</div>
            </div>
          </div>
          <template v-if="disconnectReport">
            <a-alert type="info" show-icon style="margin-bottom:12px"
              message="连接结束不一定是故障掉线；时间为后台收到记录的时间，可能晚于实际断线。记录仅保留近 24 小时、每代理最多 200 条。若代理曾转给其他用户，旧记录仍按当前归属显示。" />
            <div class="disconnect-summary">
              <strong>共 {{ disconnectReport.total }} 条</strong>
              <a-tag v-for="entry in responsibilityCounts" :key="entry.key" :color="responsibilityColor(entry.key)">
                {{ disconnectResponsibilityLabel(entry.key) }} {{ entry.count }} 条
              </a-tag>
              <span v-if="disconnectReport.items.length">最近记录：{{ disconnectReport.items[0].recorded_at }}</span>
            </div>
            <div v-if="disconnectReport.causes.length" class="disconnect-causes">
              <span>常见原因：</span>
              <a-tag v-for="entry in disconnectReport.causes.slice(0, 4)" :key="`${entry.responsibility}-${entry.cause_code}`">
                {{ disconnectCauseLabel(entry.cause_code) }} {{ entry.count }} 条
              </a-tag>
            </div>
            <div class="disconnect-report-preview">
              <strong>用户说明预览</strong>
              <p>{{ customerReportText }}</p>
            </div>
            <a-table :columns="disconnectColumns" :data-source="disconnectReport.items" row-key="id"
              size="small" :loading="disconnectLoading" :pagination="{ pageSize: 10 }"
              :scroll="{ x: 1080 }" :locale="{ emptyText: '暂无连接结束记录' }">
              <template #bodyCell="{ column, record }">
                <template v-if="column.key === 'node'">
                  <div style="font-family:ui-monospace,Menlo,monospace">{{ record.node_ip || 'IP 解析中或失败' }}</div>
                  <small>{{ record.node_name }}</small>
                </template>
                <template v-else-if="column.key === 'role'">{{ record.role_name || record.account || '未识别角色' }}</template>
                <template v-else-if="column.key === 'path'">{{ record.source_ip }} → {{ record.target_ip }}:{{ record.target_port }}</template>
                <template v-else-if="column.key === 'responsibility'">
                  <a-tag :color="responsibilityColor(record.responsibility)">{{ disconnectResponsibilityLabel(record.responsibility) }}</a-tag>
                </template>
                <template v-else-if="column.key === 'cause'">
                  <a-tooltip v-if="record.verdict_text" :title="record.verdict_text">
                    <span style="cursor:help">{{ disconnectCauseLabel(record.cause_code) }}</span>
                  </a-tooltip>
                  <template v-else>{{ disconnectCauseLabel(record.cause_code) }}</template>
                </template>
              </template>
            </a-table>
            <div class="disconnect-footnote">仅展示最新 {{ disconnectReport.items.length }} 条；汇总统计使用当前可用的全部记录。</div>
          </template>
        </a-tab-pane>
      </a-tabs>
    </template>
  </a-drawer>
</template>

<script setup lang="ts">
import { computed, defineComponent, h, ref, watch } from 'vue';
import { Button, message, Table, Tag } from 'ant-design-vue';
import { listProxies } from '../api/proxies';
import { analyzeProxyUserDisconnects, getProxyUserDisconnectAnalysisJob, getProxyUserDisconnects } from '../api/proxyUsers';
import type { ProxyUserDisconnectAnalysis, ProxyUserDisconnectResponse } from '../api/proxyUsers';
import { buildAnalysisCopyText, confidenceColor, confidenceLabel, nodeComparisonLine, sourceLabel } from './disconnectAnalysisPresentation';
import type { ProxyItem, ProxyUser } from '../types';
import { formatTrafficBytes } from '../utils/trafficFormat';
import { disconnectCauseLabel, disconnectResponsibilityLabel } from './disconnectPresentation';
import { buildProxyUserCustomerReport, buildProxyUserInternalReport, disconnectResponsibilityCounts } from './proxyUserDisconnectReport';

const props = defineProps<{
  visible: boolean;
  user: ProxyUser | null;
}>();

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  (e: 'select-proxy', proxy: ProxyItem): void;
}>();

const loading = ref(false);
const activeTab = ref('current');
const currentProxies = ref<ProxyItem[]>([]);
const deletedProxies = ref<ProxyItem[]>([]);
const disconnectLoading = ref(false);
const disconnectError = ref('');
const disconnectReport = ref<ProxyUserDisconnectResponse | null>(null);
const responsibilityCounts = computed(() => disconnectReport.value ? disconnectResponsibilityCounts(disconnectReport.value) : []);
const customerReportText = computed(() => props.user && disconnectReport.value
  ? buildProxyUserCustomerReport(props.user.wechat_nickname, disconnectReport.value) : '');
const disconnectColumns = [
  { title: '记录时间', dataIndex: 'recorded_at', width: 165 },
  { title: '公网 IP / 节点', key: 'node', width: 170 },
  { title: '角色 / 账号', key: 'role', width: 150 },
  { title: '连接路径', key: 'path', width: 225 },
  { title: '责任线索', key: 'responsibility', width: 135 },
  { title: '原因', key: 'cause', width: 180 },
  { title: '代理 ID', dataIndex: 'proxy_id', width: 85 },
];
const responsibilityColor = (value: string) => ({ node: 'red', server: 'orange', client: 'blue' } as Record<string, string>)[value] || 'default';

const statusLabel = (status: string) => ({
  active: '运行中', creating: '创建中', expired: '已过期', deleted: '已删除',
}[status] || status);

const statusColor = (status: string) => ({
  active: 'green', creating: 'blue', expired: 'orange', deleted: 'red',
}[status] || 'default');

const ProxyTable = defineComponent({
  props: {
    items: { type: Array as () => ProxyItem[], required: true },
    loading: { type: Boolean, required: true },
  },
  emits: ['select'],
  setup(tableProps, { emit: tableEmit }) {
    const columns = [
      { title: 'ID', dataIndex: 'proxy_id', width: 72 },
      { title: '节点', dataIndex: 'node_name' },
      { title: '大区', dataIndex: 'region_names' },
      { title: '状态', dataIndex: 'status' },
      { title: '到期时间', dataIndex: 'expire_at', width: 170 },
      { title: '操作', key: 'action', width: 80 },
    ];
    return () => h(Table, {
      columns,
      dataSource: tableProps.items,
      loading: tableProps.loading,
      rowKey: 'proxy_id',
      size: 'small',
      pagination: { pageSize: 10 },
    }, {
      bodyCell: ({ column, record }: { column: any; record: ProxyItem }) => {
        if (column.dataIndex === 'region_names') return (record.region_names || []).join('、') || '—';
        if (column.dataIndex === 'status') return h(Tag, { color: statusColor(record.status) }, () => statusLabel(record.status));
        if (column.key === 'action') return h(Button, { type: 'link', size: 'small', onClick: () => tableEmit('select', record) }, () => '详情');
        return undefined;
      },
    });
  },
});

const load = async () => {
  if (!props.visible || !props.user) return;
  loading.value = true;
  try {
    const response = await listProxies({ proxy_user_id: props.user.id, status: 'all' });
    const items: ProxyItem[] = response.data || [];
    currentProxies.value = items.filter((item) => item.status !== 'deleted');
    deletedProxies.value = items.filter((item) => item.status === 'deleted');
  } finally {
    loading.value = false;
  }
};

const formatBytes = formatTrafficBytes;

const loadDisconnects = async () => {
  const id = props.user?.id;
  if (!props.visible || !id) return;
  disconnectLoading.value = true;
  disconnectError.value = '';
  try {
    const response = await getProxyUserDisconnects(id);
    if (props.visible && props.user?.id === id) disconnectReport.value = response.data;
  } catch {
    if (props.visible && props.user?.id === id) {
      disconnectReport.value = null;
      disconnectError.value = '掉线记录加载失败，请重试';
    }
  } finally {
    disconnectLoading.value = false;
  }
};

const analysis = ref<ProxyUserDisconnectAnalysis | null>(null);
const analysisLoading = ref(false);
const analysisError = ref('');

const analysisPending = ref(false);
let analysisRun = 0;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const runAnalysis = async () => {
  const id = props.user?.id;
  if (!props.visible || !id) return;
  const run = ++analysisRun;
  const stillCurrent = () => run === analysisRun && props.visible && props.user?.id === id;
  analysisLoading.value = true;
  analysisPending.value = false;
  analysisError.value = '';
  try {
    const response = await analyzeProxyUserDisconnects(id);
    if (!stillCurrent()) return;
    analysis.value = response.data;
    const job = response.data.ai_status === 'pending' ? response.data.ai_job : undefined;
    analysisLoading.value = false;
    if (!job) return;
    // The model can think for a minute or more. The rule-based result is
    // already on screen; poll until the model's text is ready.
    analysisPending.value = true;
    for (let attempt = 0; attempt < 60 && stillCurrent(); attempt += 1) {
      await sleep(3000);
      if (!stillCurrent()) return;
      const polled = (await getProxyUserDisconnectAnalysisJob(id, job)).data;
      if (polled.ai_status === 'pending') continue;
      if (analysis.value) {
        analysis.value = polled.ai_status === 'done'
          ? { ...analysis.value, source: 'ai', ai_status: 'done', ai_error: undefined,
              summary: polled.summary || analysis.value.summary, customer_text: polled.customer_text || '' }
          : { ...analysis.value, ai_status: 'error', ai_error: polled.ai_error };
      }
      return;
    }
    if (stillCurrent() && analysis.value) {
      analysis.value = { ...analysis.value, ai_status: 'error', ai_error: 'AI 分析等待超时，已显示规则分析' };
    }
  } catch {
    if (stillCurrent()) analysisError.value = '分析失败，请稍后重试';
  } finally {
    if (run === analysisRun) {
      analysisLoading.value = false;
      analysisPending.value = false;
    }
  }
};

const copyAnalysis = async (kind: 'internal' | 'customer') => {
  if (!props.user || !analysis.value) return;
  const content = buildAnalysisCopyText(props.user.wechat_nickname, analysis.value, kind);
  try {
    await navigator.clipboard.writeText(content);
    message.success('已复制到剪贴板');
  } catch {
    message.error('复制失败，请检查浏览器剪贴板权限');
  }
};

const copyReport = async (kind: 'internal' | 'customer') => {
  if (!props.user || !disconnectReport.value) return;
  const content = kind === 'internal'
    ? buildProxyUserInternalReport(props.user.wechat_nickname, disconnectReport.value)
    : buildProxyUserCustomerReport(props.user.wechat_nickname, disconnectReport.value);
  try {
    await navigator.clipboard.writeText(content);
    message.success('已复制到剪贴板');
  } catch {
    message.error('复制失败，请检查浏览器剪贴板权限');
  }
};

watch(() => [props.visible, props.user?.id], () => {
  if (props.visible) {
    activeTab.value = 'current';
    disconnectReport.value = null;
    analysis.value = null;
    analysisError.value = '';
    analysisRun += 1; // stops a polling loop left over from another user
    analysisPending.value = false;
    void load();
    void loadDisconnects();
  }
});
</script>

<style scoped>
.disconnect-toolbar { display:flex; align-items:center; gap:8px; flex-wrap:wrap; margin-bottom:12px; }
.disconnect-toolbar span { color:#64748b; margin-right:auto; }
.disconnect-summary { display:flex; align-items:center; gap:8px; flex-wrap:wrap; margin-bottom:12px; }
.disconnect-summary span { color:#64748b; font-size:12px; }
.disconnect-causes { display:flex; align-items:center; gap:5px; flex-wrap:wrap; margin-bottom:12px; }
.disconnect-causes span { color:#64748b; font-size:12px; }
.disconnect-report-preview { background:#f8fafc; border:1px solid #e2e8f0; border-radius:6px; padding:10px 12px; margin-bottom:12px; }
.disconnect-report-preview p { margin:6px 0 0; line-height:1.6; white-space:pre-wrap; }
.disconnect-footnote { color:#94a3b8; font-size:12px; margin-top:8px; }
.analysis-card { background:#faf5ff; border:1px solid #e9d5ff; border-radius:6px; padding:10px 12px; margin-bottom:12px; }
.analysis-head { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
.analysis-note { color:#64748b; font-size:12px; }
.analysis-actions { margin-left:auto; display:flex; gap:6px; }
.analysis-summary { margin:8px 0; line-height:1.7; white-space:pre-wrap; }
.analysis-customer { background:#fff; border:1px solid #e2e8f0; border-radius:6px; padding:6px 10px; margin-bottom:8px; }
.analysis-customer p { margin:2px 0 0; line-height:1.6; white-space:pre-wrap; }
.analysis-hypothesis { margin-top:6px; }
.analysis-for { color:#15803d; font-size:12px; padding-left:14px; }
.analysis-against { color:#b45309; font-size:12px; padding-left:14px; }
.analysis-peers { margin-top:8px; font-size:12px; color:#475569; }
.analysis-peers small { color:#94a3b8; display:block; }
</style>
